import { useMutation } from '@apollo/client/react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Col, Row, message } from 'antd';
import clsx from 'clsx';
import { map, sortBy } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../AppContext';
import {
  AddButton,
  DeleteButton,
  DragIcon,
  EditButton,
  RightIcon,
  WrongIcon,
} from '../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../common/CommonUseLazyQuery';
import {
  BREAKPOINTS,
  BULK_ACTION,
  BULK_JOB_STATUS,
  DEFAULT_PAGE_SIZE,
  DESELECT_MESSAGE,
  GA_EVENT,
  GA_LABEL,
} from '../../../../../common/constants';
import { Event } from '../../../../../common/trackEvents';
import useRouter from '../../../../../common/useRouter';
import AddMultipleModal from '../../../../../components/AddMultipleModal';
import BulkEditStageItemDrawer from '../../../../../components/BulkEditStageItemDrawer';
import CommonBulkDeleteModal from '../../../../../components/CommonBulkDeleteModal';
import CommonCard from '../../../../../components/CommonCard';
import CommonSelectionBar from '../../../../../components/CommonSelectionBar';
import CommonTable from '../../../../../components/CommonTable';
import CommonTooltip from '../../../../../components/CommonTooltip';
import CustomCheckboxSelection from '../../../../../components/CustomCheckboxSelection';
import '../../../../../components/CustomCheckboxSelection.less';
import EllipsisText from '../../../../../components/EllipsisText';
import InfiniteScrollHandler from '../../../../../components/InfiniteScrollHandler';
import Portal from '../../../../../components/Portal';
import {
  BULK_CREATE_EQC_STAGE_ITEMS,
  BULK_DELETE_EQC_STAGE_ITEMS,
  BULK_UPDATE_EQC_TYPE_STAGE_ITEMS,
  DELETE_STAGE_ITEM,
  REORDER_STAGE_ITEM,
  UPDATE_EQC_STAGE_ITEM,
} from '../../../graphql/Mutation';
import {
  GET_BULK_EQC_TYPE_STAGE_ITEMS_JOB_STATUS,
  GET_STAGE_ITEM_LIST,
} from '../../../graphql/Queries';
import DeleteModalStage from '../DeleteModalStage';
import AddModal from './AddModal';

const RowContext = React.createContext({});

const DraggableCard = ({ id, children, className }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const [dragHandle, ...restChildren] = React.Children.toArray(
    children?.props?.children,
  );
  return (
    <CommonCard cardRef={setNodeRef} className={className} style={style}>
      <div className="d-flex">
        <div
          className="mr-5 fw-medium drag-handle"
          {...attributes}
          {...listeners}
        >
          {dragHandle}
        </div>
        {restChildren}
      </div>
    </CommonCard>
  );
};

const Rows = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    // eslint-disable-next-line react/destructuring-assignment
    id: props['data-row-key'],
  });
  const style = {
    // eslint-disable-next-line react/destructuring-assignment
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: 'relative',
          zIndex: 9999,
        }
      : {}),
  };
  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners],
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const StageItemTable = ({
  stageId,
  stageData,
  selectedRowKeys,
  setSelectedRowKeys,
  selectedRows,
  setSelectedRows,
  isSelectAllAcrossPages,
  setIsSelectAllAcrossPages,
  exceptIds,
  setExceptIds,
  onClearSelection,
  onPaginationChange,
  onBulkDeleteLoadingChange,
  isStageSelectionActive,
}) => {
  const { getCurrentUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const initialStageItemFilter = {
    skip: 0,
    limit: 10,
    sortBy: { field: 'order', order: 'ASC' },
  };
  const initialPaginationValue = {
    total: 0,
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  };
  const DragHandle = ({ disabled }) => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
      <DragIcon
        className={clsx(
          'stage-item-drag-handle',
          disabled && 'cursor-not-allowed',
        )}
        ref={setActivatorNodeRef}
        {...(!disabled && listeners)}
      />
    );
  };

  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [showModal, setShowModal] = useState(false);
  const [showAddMultipleModal, setShowAddMultipleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkEditDrawer, setShowBulkEditDrawer] = useState(false);
  const [selectedInputType, setSelectedInputType] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [stageItemData, setStageItemData] = useState();
  const [deleteStageItemModal, setDeleteStageItemModal] = useState(false);
  const [title, setTitle] = useState('');
  const [name, setName] = useState('');
  const [mutationId, setMutationId] = useState('');
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [stageItemListData, setStageItemListData] = useState([]);
  const [stageItemFilter, setStageItemFilter] = useState({
    ...initialStageItemFilter,
    eqcTypeStageId: parseInt(stageId, 10),
  });
  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);
  const updateTimeoutRefs = useRef({});
  const bulkJobsRef = useRef(new Map());
  const {
    params: { eqcTypeId },
  } = useRouter();

  const isAnyRowSelected = selectedRows?.length > 0 || isSelectAllAcrossPages;

  useEffect(() => {
    return () => {
      bulkJobsRef.current?.forEach((jobInfo) => {
        if (jobInfo?.timeoutId) {
          clearTimeout(jobInfo.timeoutId);
        }
      });
      bulkJobsRef.current?.clear();
    };
  }, []);

  const startJobTracking = (jobId, action) => {
    if (!jobId) return;
    bulkJobsRef.current?.set(jobId, { timeoutId: null, bulkAction: action });
  };

  const clearJobTracking = (jobId) => {
    if (!jobId) return;
    const jobInfo = bulkJobsRef.current?.get(jobId);
    if (jobInfo?.timeoutId) {
      clearTimeout(jobInfo.timeoutId);
    }
    bulkJobsRef.current?.delete(jobId);
  };

  const updateJobTimeout = (jobId, timeoutId) => {
    if (!jobId) return;
    const jobInfo = bulkJobsRef.current?.get(jobId);
    if (jobInfo) {
      bulkJobsRef.current?.set(jobId, { ...jobInfo, timeoutId });
    }
  };
  const [fetchStageItemData, { loading, refetch }] = useLazyQueryWithCallbacks(
    GET_STAGE_ITEM_LIST,
    {
      variables: { filter: stageItemFilter },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const data = res?.eqcTypeStageItemList?.data;
        const pagination = {
          ...paginationProp,
          total: res?.eqcTypeStageItemList?.total,
        };
        setPaginationProp(pagination);
        if (scrollFlag) {
          const datalist = [...stageItemListData, ...data];
          setStageItemListData(sortBy(datalist, ['order']));
          setScrollFlag(false);
        } else {
          const datalist = [...data];
          setStageItemListData(sortBy(datalist, ['order']));

          // Handle cross-page selection for newly loaded pages
          if (isSelectAllAcrossPages && setSelectedRows && setSelectedRowKeys) {
            // Add all new items that aren't already in selectedRows and not in exceptIds
            const existingIds = selectedRows?.map((row) => row?.id) || [];
            const newItemsToAdd =
              datalist?.filter(
                (item) =>
                  !existingIds?.includes(item?.id) &&
                  !exceptIds?.includes(item?.id),
              ) || [];

            const updatedSelectedRows = [
              ...(selectedRows || []),
              ...newItemsToAdd,
            ];
            const updatedSelectedKeys =
              updatedSelectedRows
                ?.map((row) => row?.id)
                ?.filter((id) => !exceptIds?.includes(id)) || [];

            setSelectedRows(updatedSelectedRows);
            setSelectedRowKeys(updatedSelectedKeys);
          }
        }
        setHasMore(!!data?.length);
      },
      onError() {},
    },
  );
  const handleRefetchAfterDelete = (deletedCount = 1) => {
    const currentTotal = paginationProp?.total || 0;
    const pageSize = paginationProp?.pageSize || DEFAULT_PAGE_SIZE;
    const newTotal = Math.max(0, currentTotal - deletedCount);
    const maxPage = Math.max(1, Math.ceil(newTotal / pageSize));
    const newCurrent = Math.min(paginationProp?.current || 1, maxPage);
    const newSkip = (newCurrent - 1) * pageSize;

    setStageItemFilter({
      ...stageItemFilter,
      skip: newSkip,
    });
    setPaginationProp({
      ...paginationProp,
      current: newCurrent,
    });
    fetchStageItemData({
      variables: {
        filter: {
          ...stageItemFilter,
          skip: newSkip,
        },
      },
    });
  };

  const [deleteStageItem] = useMutation(DELETE_STAGE_ITEM, {
    refetch,
    onError() {},
    onCompleted() {
      Event(GA_EVENT.DELETE_GLOBAL_CHECKLIST_STAGE_ITEM, {
        label: GA_LABEL.DELETE_GLOBAL_CHECKLIST_STAGE_ITEM,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        checklist_id: eqcTypeId,
        stage_id: stageId,
        stage_item_id: mutationId,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      setDeleteStageItemModal(false);
      handleRefetchAfterDelete();
    },
  });

  const [reorderStageItem, { loading: reorderLoading }] = useMutation(
    REORDER_STAGE_ITEM,
    {
      onCompleted() {
        Event(GA_EVENT.REORDER_GLOBAL_CHECKLIST_STAGE_ITEM, {
          label: GA_LABEL.REORDER_GLOBAL_CHECKLIST_STAGE_ITEM,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          checklist_id: eqcTypeId,
          stage_id: stageId,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
      },
      onError() {},
    },
  );

  const refetchStageDetails = () => {
    const newTotal = (paginationProp?.total || 0) + 1;
    const pageSize = paginationProp?.pageSize || DEFAULT_PAGE_SIZE;
    const lastPage = Math.ceil(newTotal / pageSize);
    const lastPageSkip = (lastPage - 1) * pageSize;

    setStageItemFilter((prev) => ({ ...prev, skip: lastPageSkip }));
    setPaginationProp((prev) => ({
      ...prev,
      total: newTotal,
      current: lastPage,
    }));
    fetchStageItemData({
      variables: {
        filter: {
          ...stageItemFilter,
          eqcTypeStageId: parseInt(stageId, 10),
          skip: lastPageSkip,
        },
      },
    });
  };

  const handleClearSelection = () => {
    if (onClearSelection) {
      onClearSelection();
    }
    setSelectedInputType(null);
    setBulkAction(null);
  };

  // Auto-clear selection when all items have been excluded
  useEffect(() => {
    if (
      isSelectAllAcrossPages &&
      exceptIds?.length >= (paginationProp?.total || 0) &&
      paginationProp?.total > 0
    ) {
      handleClearSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exceptIds, isSelectAllAcrossPages, paginationProp?.total]);

  const [getBulkJobStatus] = useLazyQueryWithCallbacks(
    GET_BULK_EQC_TYPE_STAGE_ITEMS_JOB_STATUS,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(res) {
        const jobId = res?.getBulkEqcTypeStageItemsJobStatus?.jobId;
        const state = res?.getBulkEqcTypeStageItemsJobStatus?.state;

        if (!jobId) return;

        if (state === BULK_JOB_STATUS.PROCESSING) {
          const timeoutId = setTimeout(() => {
            getBulkJobStatus({
              variables: { jobId },
            });
          }, 3000);
          updateJobTimeout(jobId, timeoutId);
        }

        if (
          state === BULK_JOB_STATUS.COMPLETED ||
          state === BULK_JOB_STATUS.FAILED
        ) {
          const jobAction = bulkJobsRef.current?.get(jobId)?.bulkAction;
          clearJobTracking(jobId);

          if (state === BULK_JOB_STATUS.COMPLETED) {
            if (jobAction === BULK_ACTION.ADD_MULTIPLE) {
              refetchStageDetails();
            } else if (jobAction === BULK_ACTION.EDIT) {
              fetchStageItemData({
                variables: {
                  filter: stageItemFilter,
                },
              });
            } else {
              handleRefetchAfterDelete(selectedRowKeys?.length);
            }
          }
        }
        handleClearSelection();
      },
    },
  );

  const handleJobStarted = (jobId, action) => {
    if (jobId) {
      startJobTracking(jobId, action);
      getBulkJobStatus({
        variables: { jobId },
      });
    }
  };

  const [bulkDeleteStageItems, { loading: bulkDeleteLoading }] = useMutation(
    BULK_DELETE_EQC_STAGE_ITEMS,
    {
      onCompleted(response) {
        const { status, jobId } = response?.bulkDeleteEqcTypeStageItems;

        if (status === 'SUCCESS' && jobId) {
          startJobTracking(jobId, BULK_ACTION.DELETE);
          getBulkJobStatus({
            variables: { jobId },
          });
        }
      },
      onError() {
        onClearSelection?.();
      },
    },
  );

  const [updateStageItem, { loading: updateLoading }] = useMutation(
    UPDATE_EQC_STAGE_ITEM,
    {
      onCompleted() {
        // Refetch current page data to stay in sync with server
        fetchStageItemData({
          variables: {
            filter: stageItemFilter,
          },
        });
      },
      onError() {},
    },
  );

  const handleBulkDelete = async () => {
    if (!selectedRowKeys?.length && !isSelectAllAcrossPages) {
      message.warning('Please select items to delete');
      return;
    }

    try {
      const mutationData = {
        isAllSelected: isSelectAllAcrossPages,
      };

      if (!isSelectAllAcrossPages) {
        mutationData.eqcTypeStageItemIds =
          selectedRows?.map((row) => row?.id) || [];
      }
      if (isSelectAllAcrossPages) {
        mutationData.eqcTypeStageId = Number(stageId);
      }
      if (isSelectAllAcrossPages && exceptIds?.length > 0) {
        mutationData.exceptEqcTypeStageItemIds = exceptIds;
      }

      await bulkDeleteStageItems({
        variables: {
          data: mutationData,
        },
      });
    } catch (error) {
      return error;
    }
  };

  // Debounced handler for updating individual stage items
  const handleStageItemUpdate = useCallback(
    (itemId, field, value) => {
      // Optimistically update the local state
      setStageItemListData(
        (prevData) =>
          prevData?.map((item) =>
            item?.id === itemId ? { ...item, [field]: value } : item,
          ) || [],
      );

      // Clear existing timeout for this item
      if (updateTimeoutRefs.current[itemId]) {
        clearTimeout(updateTimeoutRefs.current[itemId]);
      }

      // Set new timeout for API call
      updateTimeoutRefs.current[itemId] = setTimeout(() => {
        updateStageItem({
          variables: {
            id: itemId,
            data: {
              [field]: value,
            },
          },
        });
        // Clean up the timeout reference
        delete updateTimeoutRefs.current[itemId];
      }, 400);
    },
    [updateStageItem],
  );

  // Set the bulk update function reference
  // Restore selections when data changes - show which items on current page are selected
  useEffect(() => {
    if (
      selectedRows?.length > 0 &&
      stageItemListData?.length > 0 &&
      setSelectedRowKeys
    ) {
      const currentPageIds = stageItemListData?.map((item) => item?.id) || [];
      const selectedIdsOnCurrentPage =
        selectedRows
          ?.filter(
            (row) =>
              currentPageIds?.includes(row?.id) &&
              !(isSelectAllAcrossPages && exceptIds?.includes(row?.id)),
          )
          ?.map((row) => row?.id) || [];

      // Always update selectedRowKeys to show current page selections for table component
      setSelectedRowKeys(selectedIdsOnCurrentPage);
    } else if (
      selectedRows?.length === 0 &&
      stageItemListData?.length > 0 &&
      setSelectedRowKeys
    ) {
      setSelectedRowKeys([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageItemListData, selectedRows, setSelectedRowKeys]);

  useEffect(() => {
    if (onPaginationChange) {
      onPaginationChange(paginationProp);
    }
  }, [paginationProp, onPaginationChange]);

  useEffect(() => {
    if (onBulkDeleteLoadingChange) {
      onBulkDeleteLoadingChange(bulkDeleteLoading);
    }
  }, [bulkDeleteLoading, onBulkDeleteLoadingChange]);

  useEffect(() => {
    if (stageId) {
      setStageItemFilter({
        ...initialStageItemFilter,
        eqcTypeStageId: parseInt(stageId, 10),
      });
      setPaginationProp(initialPaginationValue);
      fetchStageItemData({
        variables: {
          filter: {
            ...initialStageItemFilter,
            eqcTypeStageId: parseInt(stageId, 10),
          },
        },
      });
    }
  }, [stageId]);

  useEffect(() => {
    return () => {
      Object.values(updateTimeoutRefs.current).forEach((timeout) => {
        if (timeout) {
          clearTimeout(timeout);
        }
      });
      updateTimeoutRefs.current = {};
    };
  }, []);
  const handleAddEdit = (record) => {
    if (record) {
      setStageItemData(record);
    } else {
      setStageItemData();
    }
    setShowModal(true);
  };
  const handleTableChange = (pagination) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setStageItemFilter({
      ...stageItemFilter,
      skip,
      limit: pagination.pageSize,
    });
    setPaginationProp({ ...paginationProp, ...pagination });
    fetchStageItemData({
      variables: {
        filter: {
          ...stageItemFilter,
          skip,
          limit: pagination.pageSize,
        },
      },
    });
  };
  const getActionButton = (record, index) => {
    return (
      <div
        id={`stage-item-actions-${index}`}
        className={clsx(isDesktopViewport && 'd-flex')}
      >
        <CommonTooltip
          id={`stage-item-edit-tooltip-${index}`}
          trigger="hover"
          title={isAnyRowSelected ? DESELECT_MESSAGE : 'Edit'}
        >
          <Button
            id={`stage-item-edit-btn-${index}`}
            shape="circle"
            icon={<EditButton />}
            className={clsx(
              'edit-button pointer b-0',
              isAnyRowSelected && 'disabled-button',
            )}
            disabled={isAnyRowSelected}
            onClick={() => {
              handleAddEdit(record);
            }}
          />
        </CommonTooltip>
        <CommonTooltip
          id={`stage-item-delete-tooltip-${index}`}
          trigger="hover"
          title={isAnyRowSelected ? DESELECT_MESSAGE : 'Delete'}
        >
          <Button
            id={`stage-item-delete-btn-${index}`}
            className={clsx(
              'pointer b-0',
              isAnyRowSelected && 'disabled-button',
            )}
            disabled={isAnyRowSelected}
            shape="circle"
            icon={<DeleteButton />}
            onClick={() => {
              setTitle('Stage Item');
              setName(record?.title);
              setMutationId(record?.id);
              setDeleteStageItemModal(true);
            }}
          />
        </CommonTooltip>
      </div>
    );
  };
  const columns = [
    {
      width: '5%',
      className: 'drag-visible',
      render: () => {
        return (
          <CommonTooltip
            id="stage-item-drag-tooltip"
            title="Change order"
            getPopupContainer={() =>
              // eslint-disable-next-line no-undef
              document.querySelector('.ant-table-content')
            }
          >
            <div>
              <DragHandle disabled={isAnyRowSelected} />
            </div>
          </CommonTooltip>
        );
      },
    },
    {
      title: 'ID',
      key: 'id',
      render: (_, __, index) => {
        return <div>{stageItemFilter?.skip + index + 1}</div>;
      },
    },
    {
      title: 'CHECKPOINT',
      dataIndex: 'title',
      key: 'title',
      render: (record) => (
        <div>
          <EllipsisText text={record} />
        </div>
      ),
    },
    {
      title: 'INPUT',
      dataIndex: 'type',
      key: 'type',
      render: (text, record) => {
        return (
          <div
            className={isAnyRowSelected ? 'cursor-not-allowed' : 'pointer'}
            onClick={() => !isAnyRowSelected && handleAddEdit(record)}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: 'PHOTOS',
      key: 'photo',
      render: (record) => {
        const isPhoto = record?.photo === true;
        return (
          <div
            className={clsx(
              'd-flex align-center stage-item-toggle-icon',
              isAnyRowSelected && 'cursor-not-allowed',
            )}
            onClick={() =>
              !isAnyRowSelected &&
              handleStageItemUpdate(record?.id, 'photo', !isPhoto)
            }
          >
            {isPhoto ? (
              <RightIcon
                className={clsx(
                  'stage-item-toggle-icon success-state',
                  isAnyRowSelected && 'cursor-not-allowed',
                )}
              />
            ) : (
              <WrongIcon
                className={clsx(
                  'wrong-icon stage-item-toggle-icon error-state',
                  isAnyRowSelected && 'cursor-not-allowed',
                )}
              />
            )}
          </div>
        );
      },
    },
    {
      title: 'REMARKS',
      key: 'remark',
      render: (record) => {
        const isRemark = record?.remark === true;
        return (
          <div
            className={clsx(
              'd-flex align-center stage-item-toggle-icon',
              isAnyRowSelected && 'cursor-not-allowed',
            )}
            onClick={() =>
              !isAnyRowSelected &&
              handleStageItemUpdate(record?.id, 'remark', !isRemark)
            }
          >
            {isRemark ? (
              <RightIcon
                className={clsx(
                  'stage-item-toggle-icon success-state',
                  isAnyRowSelected && 'cursor-not-allowed',
                )}
              />
            ) : (
              <WrongIcon
                className={clsx(
                  'wrong-icon stage-item-toggle-icon error-state',
                  isAnyRowSelected && 'cursor-not-allowed',
                )}
              />
            )}
          </div>
        );
      },
    },
    {
      key: 'action',
      render: (record, _, index) => getActionButton(record, index),
    },
  ];

  const DraggableBodyRow = async ({ over, active }) => {
    if (!active || !over || active?.id === over?.id) return;

    if (isAnyRowSelected) return;

    const overIndex = over?.data?.current?.sortable?.index;
    const activeIndex = active?.data?.current?.sortable?.index;
    const data = [...stageItemListData];
    setStageItemListData(arrayMove(data, activeIndex, overIndex));
    try {
      const { errors } = await reorderStageItem({
        variables: {
          data: {
            eqcTypeStageItemIds: [active?.id, over?.id],
            eqcTypeStageId: parseInt(stageId, 10),
          },
        },
      });
      if (errors) {
        throw errors;
      }
    } catch (error) {
      setStageItemListData(data);
    }
  };

  const handleRefetch = () => {
    fetchStageItemData({
      variables: {
        filter: {
          ...stageItemFilter,
          skip: stageItemListData?.length,
          limit: DEFAULT_PAGE_SIZE,
        },
      },
    });
  };

  // Selection handlers
  const handleRowSelectionChange = (selectedKeys, selectedRowsData) => {
    if (!setSelectedRows || !setSelectedRowKeys) {
      setSelectedRowKeys?.(selectedKeys);
      return;
    }

    if (isSelectAllAcrossPages) {
      const currentPageKeys = stageItemListData?.map((item) => item?.id) || [];
      const newlyDeselected =
        currentPageKeys?.filter((key) => !selectedKeys?.includes(key)) || [];
      const reSelected =
        selectedKeys?.filter((key) => exceptIds?.includes(key)) || [];
      setExceptIds?.((prev) => {
        let updated = [...prev];
        newlyDeselected?.forEach((id) => {
          if (!updated?.includes(id)) updated.push(id);
        });
        updated = updated?.filter((id) => !reSelected?.includes(id));
        return updated;
      });
      setSelectedRowKeys?.(selectedKeys);
      return;
    }

    // Get current page IDs
    const currentPageKeys = stageItemListData?.map((item) => item?.id) || [];

    // Remove current page items from existing selectedRows first (to avoid duplicates)
    const existingRowsWithoutCurrentPage =
      selectedRows?.filter((row) => !currentPageKeys?.includes(row?.id)) || [];

    // Add the new selected rows from current page (full data objects)
    const updatedSelectedRows = [
      ...existingRowsWithoutCurrentPage,
      ...selectedRowsData,
    ];

    // Update selectedRowKeys to match (just IDs for table component)
    const updatedSelectedKeys =
      updatedSelectedRows?.map((row) => row?.id) || [];

    setSelectedRows(updatedSelectedRows);
    setSelectedRowKeys(updatedSelectedKeys);
  };

  const handleSelectRow = (record, selected) => {
    if (!setSelectedRows || !setSelectedRowKeys) return;

    const recordId = record?.id;

    if (selected) {
      if (isSelectAllAcrossPages) {
        // Re-selecting a previously excluded item
        setExceptIds?.((prev) => prev?.filter((id) => id !== recordId) || []);
        setSelectedRowKeys?.((prev) => [...prev, recordId]);
        return;
      }
      // Add to selectedRows if not already included
      const isAlreadySelected = selectedRows?.some(
        (row) => row?.id === recordId,
      );
      const newSelectedRows = isAlreadySelected
        ? selectedRows
        : [...(selectedRows || []), record];
      const newSelectedKeys = newSelectedRows?.map((row) => row?.id) || [];

      setSelectedRows(newSelectedRows);
      setSelectedRowKeys(newSelectedKeys);
      return;
    }

    // Deselection logic
    if (isSelectAllAcrossPages) {
      // Keep cross-page mode ON; track excluded ID
      setExceptIds?.((prev) => [...prev, recordId]);
      const remainingKeys =
        selectedRowKeys?.filter((key) => key !== recordId) || [];
      setSelectedRowKeys?.(remainingKeys);
      return;
    }

    // Normal deselection - remove from selectedRows
    const newSelectedRows =
      selectedRows?.filter((row) => row?.id !== recordId) || [];
    const newSelectedKeys = newSelectedRows?.map((row) => row?.id) || [];
    setSelectedRows(newSelectedRows);
    setSelectedRowKeys(newSelectedKeys);
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      if (isSelectAllAcrossPages) {
        const currentPageKeys =
          stageItemListData?.map((item) => item?.id) || [];
        setExceptIds?.(
          (prev) => prev?.filter((id) => !currentPageKeys?.includes(id)) || [],
        );
        setSelectedRowKeys?.(currentPageKeys);
        return;
      }
      const allKeys = stageItemListData?.map((item) => item?.id) || [];
      setSelectedRowKeys?.(allKeys);
      setSelectedRows?.(stageItemListData || []);
    } else {
      if (isSelectAllAcrossPages) {
        const currentPageKeys =
          stageItemListData?.map((item) => item?.id) || [];
        setExceptIds?.((prev) => {
          const updated = [...prev];
          currentPageKeys?.forEach((id) => {
            if (!updated?.includes(id)) updated.push(id);
          });
          return updated;
        });
        setSelectedRowKeys?.([]);
        return;
      }
      setSelectedRowKeys?.([]);
      setSelectedRows?.([]);
    }
  };

  const handleSelectAllOnPage = () => {
    if (!setSelectedRows || !setSelectedRowKeys) return;

    const currentPageKeys = stageItemListData?.map((item) => item?.id) || [];
    setIsSelectAllAcrossPages?.(false);
    setExceptIds?.([]);

    // Remove current page items from selectedRows first, then add current page items
    const existingRowsWithoutCurrentPage =
      selectedRows?.filter((row) => !currentPageKeys?.includes(row?.id)) || [];

    const updatedSelectedRows = [
      ...existingRowsWithoutCurrentPage,
      ...(stageItemListData || []),
    ];

    const updatedSelectedKeys =
      updatedSelectedRows?.map((row) => row?.id) || [];

    setSelectedRows(updatedSelectedRows);
    setSelectedRowKeys(updatedSelectedKeys);
  };

  const handleSelectAllAcrossPages = () => {
    if (!setIsSelectAllAcrossPages) return;

    try {
      // Mark cross-page selection mode
      setExceptIds?.([]);
      setIsSelectAllAcrossPages(true);

      // Select current page items for immediate visual feedback
      const currentPageKeys = stageItemListData?.map((item) => item?.id) || [];
      setSelectedRowKeys?.(currentPageKeys);
    } catch (error) {
      message.error('Failed to select all items across pages');
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: handleRowSelectionChange,
    onSelect: handleSelectRow,
    onSelectAll: handleSelectAll,
    getCheckboxProps: (record) => ({
      disabled: false,
      name: record?.title,
    }),
    columnTitle: (
      <CustomCheckboxSelection
        checked={
          selectedRowKeys?.length === stageItemListData?.length &&
          stageItemListData?.length > 0
        }
        indeterminate={
          selectedRowKeys?.length > 0 &&
          selectedRowKeys?.length < stageItemListData?.length
        }
        disabled={!stageItemListData?.length}
        onSelectAllPage={handleSelectAllOnPage}
        onSelectAllAcross={handleSelectAllAcrossPages}
        onDeselectAll={handleClearSelection}
        currentPageCount={stageItemListData?.length}
        totalCount={paginationProp?.total}
        isSelectAllAcrossPages={isSelectAllAcrossPages}
        selectedCount={
          isSelectAllAcrossPages
            ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
            : selectedRowKeys?.length || 0
        }
      />
    ),
  };

  return (
    <>
      {showModal && (
        <AddModal
          showModal={showModal}
          setShowModal={setShowModal}
          stageData={stageItemData}
          isUpdate={!!stageItemData}
          setStageData={setStageItemData}
          refetchStageDataWithInitialValues={
            stageItemData ? handleRefetchAfterDelete : refetchStageDetails
          }
          refetchStageData={
            stageItemData ? handleRefetchAfterDelete : refetchStageDetails
          }
          stageId={stageId}
        />
      )}

      {showAddMultipleModal && (
        <AddMultipleModal
          showModal={showAddMultipleModal}
          setShowModal={setShowAddMultipleModal}
          stageId={stageId}
          stageName={stageData?.name || 'Selected'}
          refetchStageData={refetchStageDetails}
          mutation={BULK_CREATE_EQC_STAGE_ITEMS}
          mutationResponseField="bulkCreateEqcTypeStageItems"
          stageIdFieldName="eqcTypeStageId"
          analyticsEvent={GA_EVENT.BULK_ADD_GLOBAL_CHECKLIST_STAGE_ITEMS}
          analyticsLabel={GA_LABEL.BULK_ADD_GLOBAL_CHECKLIST_STAGE_ITEMS}
          analyticsContextId={eqcTypeId}
          analyticsContextIdField="checklist_id"
          modalTitle="Add Multiple Checkpoints"
          onJobStarted={(jobId) =>
            handleJobStarted(jobId, BULK_ACTION.ADD_MULTIPLE)
          }
        />
      )}

      {deleteStageItemModal && (
        <DeleteModalStage
          showModal={deleteStageItemModal}
          setShowModal={setDeleteStageItemModal}
          title={title}
          name={name}
          deleteStage={deleteStageItem}
          mutationId={mutationId}
          setMutationId={setMutationId}
        />
      )}
      <Portal portalId="stageitem-add-btn">
        <div className="d-flex">
          <CommonTooltip
            title={
              isStageSelectionActive || isAnyRowSelected ? DESELECT_MESSAGE : ''
            }
          >
            <Button
              shape="round"
              type="primary"
              id="add-btn"
              icon={<AddButton />}
              className="add-button"
              disabled={isStageSelectionActive || isAnyRowSelected}
              onClick={() => {
                handleAddEdit();
              }}
            >
              {isDesktopViewport && 'Item'}
            </Button>
          </CommonTooltip>
          <CommonTooltip
            title={
              isStageSelectionActive || isAnyRowSelected ? DESELECT_MESSAGE : ''
            }
          >
            <Button
              shape="round"
              type="default"
              id="add-bulk-btn"
              className="add-multiple-button"
              icon={<AddButton />}
              disabled={isStageSelectionActive || isAnyRowSelected}
              onClick={() => {
                setShowAddMultipleModal(true);
                setBulkAction(BULK_ACTION.ADD_MULTIPLE);
              }}
            >
              {isDesktopViewport && 'Bulk Items'}
            </Button>
          </CommonTooltip>
        </div>
      </Portal>

      {/* Professional Selection Bar - Show beside Stage Items when items are selected */}
      <Portal portalId="stage-item-selection-bar">
        {isDesktopViewport && isAnyRowSelected && (
          <CommonSelectionBar
            selectedCount={
              isSelectAllAcrossPages
                ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
                : selectedRows?.length || 0
            }
            className="ml-10 mr-10"
            onClear={handleClearSelection}
          >
            <CommonTooltip trigger="hover" title="Bulk Edit">
              <Button
                shape="round"
                icon={<EditButton />}
                className="edit-button pointer b-0"
                onClick={() => {
                  setShowBulkEditDrawer(true);
                  setBulkAction(BULK_ACTION.EDIT);
                }}
              />
            </CommonTooltip>
            <CommonTooltip trigger="hover" title="Bulk Delete">
              <Button
                className="delete-button pointer b-0 ml-5 mr-5"
                shape="round"
                icon={<DeleteButton />}
                onClick={() => {
                  setShowDeleteModal(true);
                  setBulkAction(BULK_ACTION.DELETE);
                }}
              />
            </CommonTooltip>
          </CommonSelectionBar>
        )}
      </Portal>
      {showBulkEditDrawer && (
        <BulkEditStageItemDrawer
          showDrawer={showBulkEditDrawer}
          setShowDrawer={setShowBulkEditDrawer}
          selectedInputType={selectedInputType}
          setSelectedInputType={setSelectedInputType}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          exceptIds={exceptIds}
          paginationProp={paginationProp}
          selectedRows={selectedRows}
          selectedRowKeys={selectedRowKeys}
          startJobTracking={startJobTracking}
          getBulkJobStatus={getBulkJobStatus}
          bulkAction={bulkAction}
          stageId={stageId}
          mutation={BULK_UPDATE_EQC_TYPE_STAGE_ITEMS}
          mutationResponseField="bulkUpdateEqcTypeStageItems"
          stageIdFieldName="eqcTypeStageId"
          itemIdsFieldName="eqcTypeStageItemIds"
          exceptIdsFieldName="exceptEqcTypeStageItemIds"
          containerId="stage-item-list"
        />
      )}
      <DndContext
        id="stage-item-dnd-context"
        onDragEnd={DraggableBodyRow}
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
      >
        {isDesktopViewport ? (
          <SortableContext
            id="stage-item-sortable-context-desktop"
            items={stageItemListData.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <CommonTable
              id="stage-item-table"
              components={{ body: { row: Rows } }}
              columns={columns}
              dataSource={stageItemListData || []}
              onChange={handleTableChange}
              paginationConfig={paginationProp}
              rowKey="id"
              loading={loading || reorderLoading || updateLoading}
              rowSelection={isDesktopViewport ? rowSelection : undefined}
              className="dq-stage-item-table eqc-module"
              rowClassName={() => {
                const classes = ['eqc-table-row'];
                if (isAnyRowSelected) {
                  classes.push('dq-selected-row', 'cursor-not-allowed');
                }
                return classes.join(' ');
              }}
            />
          </SortableContext>
        ) : (
          <InfiniteScrollHandler
            id="stage-item-infinite-scroll"
            scrollFlag={scrollFlag}
            loading={loading || reorderLoading || updateLoading}
            refetchData={handleRefetch}
            setScrollFlag={setScrollFlag}
            hasMore={hasMore}
            wrapperClassName="stage-item-scroll-wrapper"
            dataLength={stageItemListData?.length}
            skeletonRows={columns?.length - 4}
          >
            <SortableContext
              id="stage-item-sortable-context-mobile"
              items={stageItemListData}
              strategy={rectSortingStrategy}
            >
              {map(stageItemListData, (item, index) => {
                return (
                  <DraggableCard
                    className="stage-card"
                    id={item?.id}
                    key={item?.id}
                  >
                    <div
                      id={`stage-item-card-content-${index}`}
                      className="common-card d-flex"
                    >
                      <DragIcon id={`stage-item-drag-icon-${index}`} />
                      <div id={`stage-item-details-${index}`}>
                        <div
                          id={`stage-item-header-${index}`}
                          className="card-header fw-medium"
                        >
                          <Row wrap={false}>
                            <Col flex="auto">
                              <span
                                id={`stage-item-number-${index}`}
                                className="mr-5"
                              >
                                {index + 1}.
                              </span>
                              {item?.title}
                            </Col>
                          </Row>
                        </div>
                        <div
                          id={`stage-item-content-${index}`}
                          className="card-content text-secondary"
                        >
                          <br />
                          <div
                            id={`stage-item-input-${index}`}
                            className="mb-15"
                          >
                            <span className="fw-medium mr-5">Input:</span>
                            <span
                              className="stage-item-input-link"
                              onClick={() => handleAddEdit(item)}
                            >
                              {item?.type}
                            </span>
                          </div>
                          <div
                            id={`stage-item-photos-${index}`}
                            className="mb-15 d-flex align-center"
                          >
                            <span className="fw-medium mr-5">Photos:</span>
                            <div
                              className="stage-item-toggle-icon"
                              onClick={() =>
                                handleStageItemUpdate(
                                  item?.id,
                                  'photo',
                                  !item?.photo,
                                )
                              }
                            >
                              {item?.photo === true ? (
                                <RightIcon className="stage-item-toggle-icon success-state" />
                              ) : (
                                <WrongIcon className="wrong-icon stage-item-toggle-icon error-state" />
                              )}
                            </div>
                          </div>
                          <div
                            id={`stage-item-remarks-${index}`}
                            className=" d-flex align-center"
                          >
                            <span className="fw-medium mr-5">Remarks:</span>
                            <div
                              className="stage-item-toggle-icon"
                              onClick={() =>
                                handleStageItemUpdate(
                                  item?.id,
                                  'remark',
                                  !item?.remark,
                                )
                              }
                            >
                              {item?.remark === true ? (
                                <RightIcon className="stage-item-toggle-icon success-state" />
                              ) : (
                                <WrongIcon className="wrong-icon stage-item-toggle-icon error-state" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <span
                        id={`stage-item-actions-mobile-${index}`}
                        className="position-absolute stage-item-action-btn"
                      >
                        {getActionButton(item, index)}
                      </span>
                    </div>
                  </DraggableCard>
                );
              })}
            </SortableContext>
          </InfiniteScrollHandler>
        )}
      </DndContext>

      {/* Bulk Delete Confirmation Modal */}
      {showDeleteModal && (
        <CommonBulkDeleteModal
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
          selectedCount={
            isSelectAllAcrossPages
              ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
              : selectedRows?.length || 0
          }
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          totalCount={paginationProp?.total || 0}
          onConfirm={handleBulkDelete}
          loading={bulkDeleteLoading}
          entityName="stage item"
          entityNamePlural="stage items"
        />
      )}
    </>
  );
};

export default StageItemTable;
